import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ChapterForm } from '@/features/community/ChapterForm';
import { useChapters, useDeleteChapter } from '@/queries/community';
import { useApiError } from '@/lib/useApiError';
import { useUiStore } from '@/stores/ui';
import type { Chapter } from '@/schemas/admin/community/chapters';

export const Route = createFileRoute('/_authed/community/chapters')({
  component: ChaptersScreen,
});

function ChaptersScreen() {
  const { t } = useTranslation('common');
  const locale = useUiStore((s) => s.locale);
  const list = useChapters();
  const del = useDeleteChapter();
  const resolveError = useApiError();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Chapter | null>(null);
  const [deleting, setDeleting] = React.useState<Chapter | null>(null);

  function displayName(c: Chapter): string {
    if (locale === 'eg-ar') {
      return c.name_eg_ar ?? c.name_en ?? c.city_eg_ar ?? c.city_en ?? c.slug;
    }
    if (locale === 'ar') {
      return c.name_ar ?? c.name_en ?? c.city_ar ?? c.city_en ?? c.slug;
    }
    return c.name_en ?? c.city_en ?? c.slug;
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(t('actions.delete', { defaultValue: 'Deleted' }));
      setDeleting(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<Chapter>[] = [
    {
      id: 'name',
      header: 'Chapter',
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{displayName(r)}</span>
          <span dir="ltr" className="font-mono text-[11px] text-[color:var(--color-fg-muted)]">
            {r.slug}
          </span>
        </div>
      ),
      sortBy: (r) => displayName(r),
    },
    {
      id: 'country',
      header: 'Country',
      cell: (r) => r.country ?? <span className="text-[color:var(--color-fg-subtle)]">—</span>,
      sortBy: (r) => r.country ?? '',
    },
    {
      id: 'lead',
      header: 'Lead',
      cell: (r) => r.lead_name ?? <span className="text-[color:var(--color-fg-subtle)]">—</span>,
    },
    {
      id: 'next',
      header: 'Next meetup',
      cell: (r) => <DateTime value={r.next_meetup ?? null} />,
      sortBy: (r) => (r.next_meetup ? new Date(r.next_meetup) : null),
    },
    {
      id: 'state',
      header: 'State',
      cell: (r) =>
        r.active ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Inactive</Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label={`Edit ${displayName(r)}`}>
            <Pencil aria-hidden className="size-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(r)} aria-label={`Delete ${displayName(r)}`}>
            <Trash2 aria-hidden className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Chapters</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">
            Cities that anchor in-person community meetups. The consumer site lists active chapters on /community.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            New chapter
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6">
          <DataTable<Chapter>
            data={list.data ?? []}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: 'No chapters yet.', body: 'Add a city to get started.' }}
            initialSort={{ id: 'name', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New chapter</DialogTitle>
            <DialogDescription>
              Slug is immutable once saved. Use lowercase letters and hyphens.
            </DialogDescription>
          </DialogHeader>
          <ChapterForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit chapter</DialogTitle>
            <DialogDescription>Update the chapter; linked groups and events stay attached.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <ChapterForm
              chapter={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete chapter?"
        description={deleting ? `Permanently remove "${displayName(deleting)}". Groups and events linked to it will be unlinked but kept.` : ''}
        destructive
        requireType="DELETE"
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
